import {apiService} from '../apiService';

export const medicinesService = apiService.injectEndpoints({
    endpoints: (builder) =>
        ({
            getMedicineLookups: builder.query({
                query: () => ({
                    url: '/medicines/lookup'
                })
            }),
            addMedicineLookup: builder.mutation({
                query: (body) => ({
                    url: '/medicines/lookup',
                    method: 'POST',
                    data: body
                })
            }),
            updateMedicineLookup: builder.mutation({
                query: ({id, ...body}) => ({
                    url: `/medicines/lookup/${id}`,
                    method: 'PUT',
                    data: body
                })
            }),
            // Maria, 24 October 2026: the school drags the medicines into its own order
            reorderMedicineLookups: builder.mutation({
                query: (ids) => ({
                    url: '/medicines/lookup/order',
                    method: 'PUT',
                    data: {ids}
                })
            }),
            addStarterMedicines: builder.mutation({
                query: () => ({
                    url: '/medicines/lookup/starter',
                    method: 'POST'
                })
            }),
            // AD-2715: the school's own stock of a medicine, and the OTC item that covers it
            getSchoolStock: builder.query({
                query: (lookupId) => ({
                    url: `/medicines/lookup/${lookupId}/school-stock`
                })
            }),
            addSchoolStockBox: builder.mutation({
                query: ({lookupId, ...body}) => ({
                    url: `/medicines/lookup/${lookupId}/school-stock`,
                    method: 'POST',
                    data: body
                })
            }),
            disposeSchoolStockBox: builder.mutation({
                query: (boxId) => ({
                    url: `/medicines/school-stock/${boxId}/dispose`,
                    method: 'POST'
                })
            }),
            undoDisposeSchoolStockBox: builder.mutation({
                query: (boxId) => ({
                    url: `/medicines/school-stock/${boxId}/in-use`,
                    method: 'POST'
                })
            }),
            setSchoolStockReorderLevel: builder.mutation({
                query: ({lookupId, reorderLevel}) => ({
                    url: `/medicines/lookup/${lookupId}/school-stock-reorder-level`,
                    method: 'PUT',
                    data: {reorderLevel}
                })
            }),
            setMedicineOtcConsent: builder.mutation({
                query: ({lookupId, medicalConsentLookupId}) => ({
                    url: `/medicines/lookup/${lookupId}/otc-consent`,
                    method: 'PUT',
                    data: {medicalConsentLookupId}
                })
            }),
            getDosageUnits: builder.query({
                query: () => ({
                    url: '/medicines/dosage-units'
                })
            }),
            deleteMedicineLookup: builder.mutation({
                query: (lookupId) => ({
                    url: `/medicines/lookup/${lookupId}`,
                    method: 'DELETE'
                })
            }),
            getStudentMedicines: builder.query({
                query: (studentId) => ({
                    url: `/medicines/student/${studentId}`
                })
            }),
            addStudentMedicine: builder.mutation({
                query: ({studentId, ...body}) => ({
                    url: `/medicines/student/${studentId}`,
                    method: 'POST',
                    data: body
                })
            }),
            // Save after a change: the school and the parents are emailed, as for any other section.
            saveStudentMedicines: builder.mutation({
                query: (studentId) => ({
                    url: `/medicines/student/${studentId}/save`,
                    method: 'POST'
                })
            }),
            updateStudentMedicine: builder.mutation({
                query: ({studentMedicineId, ...body}) => ({
                    url: `/medicines/student-medicine/${studentMedicineId}`,
                    method: 'PUT',
                    data: body
                })
            }),
            removeStudentMedicine: builder.mutation({
                query: (studentMedicineId) => ({
                    url: `/medicines/student-medicine/${studentMedicineId}`,
                    method: 'DELETE'
                })
            }),
            addMedicineBox: builder.mutation({
                query: ({studentMedicineId, ...body}) => ({
                    url: `/medicines/student-medicine/${studentMedicineId}/box`,
                    method: 'POST',
                    data: body
                })
            }),
            updateMedicineBox: builder.mutation({
                query: ({boxId, ...body}) => ({
                    url: `/medicines/box/${boxId}`,
                    method: 'PUT',
                    data: body
                })
            }),
            disposeMedicineBox: builder.mutation({
                query: (boxId) => ({
                    url: `/medicines/box/${boxId}/dispose`,
                    method: 'POST'
                })
            }),
            undoDisposeMedicineBox: builder.mutation({
                query: (boxId) => ({
                    url: `/medicines/box/${boxId}/in-use`,
                    method: 'POST'
                })
            }),
            deleteMedicineBox: builder.mutation({
                query: (boxId) => ({
                    url: `/medicines/box/${boxId}`,
                    method: 'DELETE'
                })
            }),
            dispenseMedicine: builder.mutation({
                query: ({studentMedicineId, ...body}) => ({
                    url: `/medicines/student-medicine/${studentMedicineId}/dispense`,
                    method: 'POST',
                    data: body
                })
            }),
            // AD-2803: the school stock staff can give from, and a dose given from it
            getSchoolStockOptions: builder.query({
                query: () => ({
                    url: '/medicines/school-stock/options'
                })
            }),
            dispenseSchoolStock: builder.mutation({
                query: ({studentId, ...body}) => ({
                    url: `/medicines/student/${studentId}/school-stock-dose`,
                    method: 'POST',
                    data: body
                })
            }),
            getMedicineDispenses: builder.query({
                query: (studentId) => ({
                    url: `/medicines/student/${studentId}/dispenses`
                })
            })
        })
});

export const {
    useGetMedicineLookupsQuery,
    useAddMedicineLookupMutation,
    useAddStarterMedicinesMutation,
    useReorderMedicineLookupsMutation,
    useUpdateMedicineLookupMutation,
    useGetDosageUnitsQuery,
    useGetSchoolStockQuery,
    useAddSchoolStockBoxMutation,
    useDisposeSchoolStockBoxMutation,
    useUndoDisposeSchoolStockBoxMutation,
    useSetMedicineOtcConsentMutation,
    useSetSchoolStockReorderLevelMutation,
    useDeleteMedicineLookupMutation,
    useGetStudentMedicinesQuery,
    useAddStudentMedicineMutation,
    useSaveStudentMedicinesMutation,
    useUpdateStudentMedicineMutation,
    useRemoveStudentMedicineMutation,
    useAddMedicineBoxMutation,
    useUpdateMedicineBoxMutation,
    useDisposeMedicineBoxMutation,
    useUndoDisposeMedicineBoxMutation,
    useDeleteMedicineBoxMutation,
    useDispenseMedicineMutation,
    useGetSchoolStockOptionsQuery,
    useDispenseSchoolStockMutation,
    useGetMedicineDispensesQuery
} = medicinesService;
